import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { Map as MapLibreMap } from 'maplibre-gl';
import type { Theme } from '@mui/material';
import {
  Alert, Box, Button, CircularProgress, IconButton, List, ListItemButton, ListItemText,
  Paper, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography, useMediaQuery,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';

import { api, ApiError, basemapUrlFor } from '../api/client';
import type {
  City, SavedRoute, SavedRouteSummary, SavedTrip, SavedTripSummary,
} from '../api/types';
import { ItineraryPanel } from '../components/ItineraryPanel';
import { PinnedStopDialog } from '../components/PinnedStopDialog';
import { MapView } from '../components/MapView';
import { MapOnlyToggle } from '../components/MapOnlyToggle';
import { TripDayStrip } from '../components/TripDayStrip';
import { colourForDay } from '../theme';
import { PlaceDetailsDialog } from '../components/PlaceDetailsDialog';
import { ExportRouteButton } from '../components/ExportRouteButton';
import { useSession } from '../components/RequireAuth';
import { useWhom } from '../features/people/whom';
import { dayPlanRequest, directionsForPlan } from '../export/dayRequest';
import { MODE_LABELS, type TravelMode } from '../api/types';

/**
 * Who saved a route and when, as one line.
 *
 * The name is omitted rather than faked for a route saved before it was
 * recorded, or by the automation token, which has no account behind it —
 * "Saved by" followed by nothing reads as a bug, and inventing a name reads
 * as a lie. The date still stands on its own.
 */
function savedLine(savedBy: string, updatedAt: string): string {
  const when = new Date(updatedAt);
  const stamp = Number.isNaN(when.getTime())
    ? ''
    : when.toLocaleString(undefined, {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });

  if (savedBy === 'You') return stamp ? `Saved by you · ${stamp}` : 'Saved by you';
  if (savedBy && stamp) return `Saved by ${savedBy} · ${stamp}`;
  if (savedBy) return `Saved by ${savedBy}`;

  return stamp ? `Saved ${stamp}` : '';
}

/** "Day 2 - Sat 10 Oct", or just the number where there is no date. */
function tripDayLabel(index: number, date: string | null): string {
  const named = date
    ? new Date(`${date}T00:00:00`).toLocaleDateString(undefined, {
      weekday: 'short', day: 'numeric', month: 'short',
    })
    : '';

  return named ? `Day ${index + 1} \u2014 ${named}` : `Day ${index + 1}`;
}

export function SavedRoutesPage() {
  /*
    Who saved it, where there is a who.

    With the login off every row is written against the seeded account — the
    records need a name — but there are no operators to tell apart on
    screen, and "Saved by admin" on every line of a list is a column of the
    same word. The date is the part that still says something. Rows saved
    before the login was turned off are treated the same way, because what
    the reader can act on is whether this deployment HAS accounts, not
    whether it once did.
  */
  const { session } = useSession();
  const whom = useWhom();
  /*
    Whose it is, in the second person where it is the reader's.

    With the list scoped to the account that is nearly every row, and
    "Saved by Priya Raman" on her own work reads as somebody else's. Empty
    where there are no accounts at all: see the caption below.
  */
  const who = (savedBy: string) => (
    session.authDisabled ? '' : whom(savedBy)
  );

  /*
    Whose saved work is listed.

    Mine by default, because that is what somebody opens this page for. The
    other question — what has anybody planned here — is the one the coverage
    panel answers a place at a time, and it is worth being able to ask of the
    whole list: to find somebody else's round before planning over the same
    ground, or to open the plan a coverage row just named.
  */
  const [everyone, setEveryone] = useState(false);
  const [routes, setRoutes] = useState<SavedRouteSummary[] | null>(null);
  /*
   * The multi-day operations, which this page did not list at all.
   *
   * Saving one worked — the row went to the database and came back with an
   * id — and then there was nowhere it appeared. The whole API for them is
   * here (list, open, delete) and the one screen called "Saved" asked only
   * for single days, so pressing Save on a trip looked exactly like pressing
   * a button that does nothing.
   */
  const [trips, setTrips] = useState<SavedTripSummary[] | null>(null);
  /*
   * An operation being read, and which of its days.
   *
   * Opened HERE rather than in the planner. It used to navigate away to the
   * multi-day form, which is a different screen with a different job: a saved
   * operation is a record of what was done, and reading one should look like
   * reading a saved route — the same three panels, the same way back, the
   * same map. Going to the planner instead made the two kinds of saved thing
   * behave nothing alike.
   */
  const [selectedTrip, setSelectedTrip] = useState<SavedTrip | null>(null);
  const [tripDay, setTripDay] = useState(0);
  const [selected, setSelected] = useState<SavedRoute | null>(null);
  const [city, setCity] = useState<City | null>(null);
  const [error, setError] = useState<string | null>(null);
  /*
   * The stop being looked at, and the one being pointed at.
   *
   * A saved route is read rather than edited, and reading is exactly when
   * somebody asks "which of these is the Navy Yard one?" — so the names open
   * their place, and hovering a stop lights it on the map as it does while
   * planning. Nothing here changes the route; there is nothing to change it
   * back to.
   */
  const [detailsPoiId, setDetailsPoiId] = useState<string | null>(null);
  /**
   * Kept, not just fetched.
   *
   * This page asked for the settings and threw the answer away, relying on the
   * side effect that caches the shared-basemap flag inside the client. That
   * covered the basemap and nothing else: with no satellite URL to hand to the
   * map, switching to satellite changed the state and the address bar while
   * the imagery was never applied.
   */
  const [settings, setSettings] = useState<Record<string, unknown>>({});
  const [highlighted, setHighlighted] = useState<number | null>(null);
  /** Which stop of the round on screen the pinned-stop panel is describing. */
  const [pinIndex, setPinIndex] = useState<number | null>(null);
  /*
   * Whether the list of saved routes is showing.
   *
   * Three panels leave the map a narrow strip on anything but a wide screen,
   * and once a route is open the list has done its job — so it folds away and
   * gives the space to the map.
   */
  /*
   * The live map, held only so the export can take a picture of it.
   *
   * A ref rather than state: nothing on this page re-renders because the map
   * finished loading, and making it state would redraw everything for the
   * benefit of a button that is read once, on a click.
   */
  const exportMap = useRef<MapLibreMap | null>(null);

  const [listOpen, setListOpen] = useState(true);
  /*
   * Which half of an opened route a phone is showing.
   *
   * Splitting the screen gave a cramped itinerary and a small map and did
   * neither well, so they take turns at full height — the same way the planner
   * does. Above the breakpoint both are on screen and this is ignored.
   */
  const [mobileView, setMobileView] = useState<'itinerary' | 'map'>('itinerary');

  /*
   * Whether both halves fit side by side.
   *
   * Used to decide what to *render*, not merely what to show. A MapLibre map
   * built inside a hidden container measures zero and settles at its fallback
   * size, and neither a resize event nor a ResizeObserver brings it back —
   * so the map is mounted only when it will actually be on screen.
   */
  const wide = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'));

  /*
   * The one itinerary being read, whichever kind of saved thing it came from.
   *
   * A route is one plan; an operation is several, of which a day is chosen.
   * Everything below this line treats them the same, which is the point —
   * the panels, the map and the way back should not know the difference.
   */
  const viewingDay = selectedTrip?.plan.days[tripDay] ?? null;

  /*
   * The turns for whatever is on screen, for the itinerary's own offer.
   *
   * A saved route has its own request; a saved operation's day has one
   * rebuilt from the operation's defaults, the same way the export does it.
   */
  const itineraryDirections = useMemo(() => {
    if (selected) return directionsForPlan(selected.request, selected.plan);
    if (selectedTrip && viewingDay?.plan) {
      const dayReq = dayPlanRequest(selectedTrip.request, tripDay, viewingDay.plan);
      if (dayReq) return directionsForPlan(dayReq, viewingDay.plan);
    }

    return null;
  }, [selected, selectedTrip, viewingDay, tripDay]);

  /* Every planned day of a saved operation, as the export wants them. */
  const tripExportDays = useMemo(() => {
    if (!selectedTrip) return [];

    return selectedTrip.plan.days.flatMap((d, i) => {
      if (!d.plan) return [];
      const dayReq = dayPlanRequest(selectedTrip.request, i, d.plan);

      return dayReq
        ? [{
            label: tripDayLabel(i, d.date),
            request: dayReq,
            plan: d.plan,
            ...(d.date ? { date: d.date } : {}),
          }]
        : [];
    });
  }, [selectedTrip]);
  const viewingPlan = selected?.plan ?? viewingDay?.plan ?? null;
  const anythingOpen = Boolean(selected ?? selectedTrip);

  /*
   * Which day, drawn once and placed twice.
   *
   * Over the map where the two share the screen, and inside the itinerary
   * where they take turns — because below that breakpoint the map REPLACES
   * the column, and a strip that lived only on the map could be reached only
   * from the view whose whole purpose is to have stopped reading.
   */
  const dayStrip = selectedTrip ? (
    <TripDayStrip
      days={selectedTrip.plan.days}
      selected={tripDay}
      onSelect={(index) => { setTripDay(index); setHighlighted(null); }}
      colourFor={colourForDay}
      stale={[]}
      busyIndex={null}
    />
  ) : null;

  /* The days not being looked at, drawn faintly so the week has a shape. */
  const otherDays = (selectedTrip?.plan.days ?? [])
    .filter((d) => d.plan && d.index !== tripDay)
    .map((d) => ({
      index: d.index,
      colour: colourForDay(d.index),
      geometry: d.plan!.legs.map((leg) => leg.geometry),
    }));

  /*
   * Whether anything is showing beside the map.
   *
   * This page could fold its LIST away and not its itinerary, so on a wide
   * screen there was no way to reach the map on its own — the one thing
   * somebody reading a saved route most often wants. The planners both have
   * the control; this had the same three panels and none of it.
   */
  const [panelsOpen, setPanelsOpen] = useState(true);
  const showMap = wide || mobileView === 'map';
  const showItinerary = (wide || mobileView === 'itinerary') && panelsOpen;
  /*
   * Folding also puts a narrow screen on the map, so that between the two
   * breakpoints — where the panes take turns rather than sharing — the fold
   * cannot leave both of them hidden.
   */
  const toggleMapOnly = () => {
    if (panelsOpen) {
      setMobileView('map');
      setPanelsOpen(false);
    } else {
      setPanelsOpen(true);
      setMobileView('itinerary');
    }
  };

  const load = () =>
    api
      .savedRoutes(everyone)
      .then(setRoutes)
      .catch((err: ApiError) => {
        setError(err.message);
        setRoutes([]);
      });

  // Fetched apart from the routes: a trip failing to list is no reason to
  // show nothing, and the two are separate calls anyway.
  const loadTrips = () =>
    api
      .trips(everyone)
      .then(setTrips)
      .catch(() => setTrips([]));

  /* Asked again when the scope changes; the lists are two small calls. */
  useEffect(() => {
    void load();
    void loadTrips();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [everyone]);

  useEffect(() => {
    void load();
    void loadTrips();
    /*
     * The map's own configuration: which basemap archive covers this city,
     * and where satellite imagery comes from. A saved route opened directly is
     * the first page some visits load, so nothing else has fetched these.
     */
    void api.settings().then(setSettings).catch(() => undefined);
  }, []);

  /*
     * Opened on one record, from a link somewhere else.
     *
     * The list beside this shows only what THIS browser saved, because that
     * is what owner_key records; the coverage tab reports on everyone's saved
     * work for the city and links here. Both fetches are by id alone, so a
     * link to somebody else's round opens it even though the list cannot
     * show it. Read once: afterwards the screen is driven by what is clicked.
     */
  const [params] = useSearchParams();
  const asked = useRef(false);

  /*
   * A stop picked in the list, for the map to go to.
   *
   * The nonce is what makes picking the same stop twice move the map again;
   * without it the second click is a no-op, which reads as the list having
   * stopped working. Same shape the planner uses.
   */
  const [focusStop, setFocusStop] = useState<{ index: number; nonce: number } | null>(null);

  /*
   * Whether the places the round only passes are put down.
   *
   * Held here rather than in the map because the schedule hides them in
   * step: one switch, two panels, so the list and the line always agree
   * about what is being shown. Starts put down, and only means anything
   * where there are enough of them to be in the way.
   */
  const [passedHidden, setPassedHidden] = useState(true);

  const focusOnMap = (index: number) => {
    setHighlighted(index);
    setFocusStop((current) => ({ index, nonce: (current?.nonce ?? 0) + 1 }));
  };

  const open = async (id: string) => {
    try {
      setMobileView('itinerary');
      const route = await api.savedRoute(id);
      setSelectedTrip(null);
      setSelected(route);
      setCity(await api.city(route.cityId));
    } catch (err) {
      setError((err as ApiError).message);
    }
  };

  const openTrip = async (id: string) => {
    try {
      setMobileView('itinerary');
      const trip = await api.trip(id);
      setSelected(null);
      setSelectedTrip(trip);
      // The first day that was actually planned: opening on a day that failed
      // shows an empty map and says nothing about why.
      const first = trip.plan.days.findIndex((d) => d.plan);
      setTripDay(first >= 0 ? first : 0);
      setCity(await api.city(trip.cityId));
    } catch (err) {
      setError((err as ApiError).message);
    }
  };

  /*
   * A record asked for in the address, opened once.
   *
   * ?route=<id> or ?trip=<id>&day=<n>, which is what the coverage tab links
   * to. Guarded by a ref rather than by the dependency list because `open`
   * and `openTrip` are rebuilt every render; without it this would reopen the
   * record each time anything on the page changed, throwing away whichever
   * day somebody had since chosen.
   */
  useEffect(() => {
    if (asked.current) return;
    const routeId = params.get('route');
    const tripId = params.get('trip');
    if (!routeId && !tripId) return;

    asked.current = true;
    if (routeId) {
      void open(routeId);

      return;
    }
    if (!tripId) return;

    void openTrip(tripId).then(() => {
      // The day is 1-based in the link, as it is on the screen.
      const day = Number(params.get('day'));
      if (Number.isInteger(day) && day > 0) setTripDay(day - 1);
    });
  }, [params]);

  /** Back to the list, whichever kind of thing was open. */
  const clearSelection = () => {
    setSelected(null);
    setSelectedTrip(null);
  };

  const removeTrip = async (id: string, event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await api.deleteTrip(id);
      if (selectedTrip?.id === id) setSelectedTrip(null);
      await loadTrips();
    } catch (err) {
      setError((err as ApiError).message);
    }
  };

  const remove = async (id: string, event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await api.deleteSavedRoute(id);
      if (selected?.id === id) setSelected(null);
      await load();
    } catch (err) {
      setError((err as ApiError).message);
    }
  };

  if (routes === null) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100%', minHeight: 0 }}>
      {/*
        Three fixed panels side by side need about 1200px. Below that they are
        shown one at a time: the list until a route is chosen, then the route
        itself with a way back.
      */}
      <Paper
        variant="outlined"
        sx={{
          width: { xs: '100%', md: 380 },
          flexShrink: 0,
          borderRadius: 0,
          borderTop: 0,
          borderBottom: 0,
          borderLeft: 0,
          overflowY: 'auto',
          display: {
            /* Either kind, not just a route: on a phone the list and what
               was opened cannot share the screen, and an operation opened
               while the list stayed put left the two stacked on top of each
               other with no way to tell which was in front. */
            xs: anythingOpen ? 'none' : 'block',
            md: listOpen && panelsOpen ? 'block' : 'none',
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Stack direction="row" spacing={0.75} sx={{ alignItems: 'flex-start' }}>
            <Typography variant="h2" gutterBottom>Saved routes</Typography>
            {/*
              Whose list this is, on the mark rather than in the margin.

              It used to be the browser's: the key in local storage was the
              owner, so signing in from another machine showed nothing and
              anybody sitting at this one saw everything. It is the
              account's now, and that had to be said — but it is read once
              and then in the way, two lines of explanation above a list
              somebody opened to find a plan in. So it is said here, where
              it can still be asked for.
            */}
            <Tooltip
              title={session.authDisabled
                ? 'Saved on this device only — clearing site data removes them.'
                : everyone
                  ? 'Every operator’s saved work for this deployment, newest first.'
                  : 'Your saved work, wherever you sign in. Plans saved before you had '
                    + 'an account stay with this browser.'}
            >
              <InfoOutlinedIcon
                sx={{ fontSize: 16, color: 'text.disabled', mt: 0.5, cursor: 'help' }}
              />
            </Tooltip>
            <Box sx={{ flex: 1 }} />
            <Tooltip title="Hide this list">
              <IconButton
                size="small"
                onClick={() => setListOpen(false)}
                aria-label="Hide the saved routes list"
                sx={{ display: { xs: 'none', md: 'inline-flex' }, mt: -0.5 }}
              >
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
          {/*
            Mine, or everybody's.

            Offered only where there are accounts to tell apart: with the
            login off every row was saved by the same operator, and a switch
            between "mine" and "everyone" would be a switch between one list
            and the same list.
          */}
          {!session.authDisabled && (
            <ToggleButtonGroup
              exclusive
              size="small"
              value={everyone ? 'all' : 'mine'}
              onChange={(_event, next: string | null) => {
                if (next) setEveryone(next === 'all');
              }}
              sx={{ mt: 1 }}
            >
              <ToggleButton value="mine" sx={{ textTransform: 'none', px: 1.25 }}>
                Mine
              </ToggleButton>
              <ToggleButton value="all" sx={{ textTransform: 'none', px: 1.25 }}>
                Everyone
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        </Box>

        {error && <Alert severity="error" sx={{ mx: 2, mb: 2 }}>{error}</Alert>}

        {/* Empty only when BOTH are: a page listing three operations should
            not also say nothing is saved. */}
        {routes.length === 0 && (trips?.length ?? 0) === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
            Nothing saved yet. Plan a route or an operation and press Save.
          </Typography>
        ) : (
          <List dense disablePadding>
            {routes.map((route) => (
              <ListItemButton
                key={route.id}
                selected={selected?.id === route.id}
                onClick={() => open(route.id)}
              >
                <ListItemText
                  primary={route.name}
                  /*
                    What it is, then who saved it and when.

                    Two lines rather than one longer one: the first answers
                    "is this the round I want", the second "is this the one I
                    saved, or the one somebody else did". A list keyed on the
                    browser could only ever answer the first, which is why
                    neither was shown.
                  */
                  secondary={(
                    <>
                      <Box component="span" sx={{ display: 'block' }}>
                        {`${route.cityName} · `
                          + `${MODE_LABELS[route.mode as TravelMode] ?? route.mode} · `
                          + `${route.stopCount} stops · `
                          + `${Math.round(route.totalMinutes / 60)} hr`}
                      </Box>
                      <Box component="span" sx={{ display: 'block' }}>
                        {savedLine(who(route.savedBy), route.updatedAt)}
                      </Box>
                    </>
                  )}
                  slotProps={{ primary: { noWrap: true }, secondary: { noWrap: true } }}
                />
                <IconButton size="small" edge="end" onClick={(e) => remove(route.id, e)}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </ListItemButton>
            ))}
          </List>
        )}

        {trips && trips.length > 0 && (
          <>
            <Box sx={{ px: 2, pt: 2.5, pb: 0.5 }}>
              <Typography variant="h2" sx={{ fontSize: '1.05rem' }}>
                Saved operations
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Several days each. Choose a day once one is open.
              </Typography>
            </Box>
            <List dense disablePadding>
              {trips.map((trip) => (
                <ListItemButton
                  key={trip.id}
                  selected={selectedTrip?.id === trip.id}
                  onClick={() => void openTrip(trip.id)}
                >
                  <ListItemText
                    primary={trip.name}
                    /* Who saved it and when, as a saved route has said all
                       along — an operation is somebody's work too. */
                    secondary={(
                      <>
                        <Box component="span" sx={{ display: 'block' }}>
                          {`${trip.cityName} · ${trip.dayCount} day${trip.dayCount === 1 ? '' : 's'} · `
                            + `${trip.stopCount} stops`
                            + (trip.plannedDays < trip.dayCount
                              ? ` · ${trip.dayCount - trip.plannedDays} not planned` : '')}
                        </Box>
                        <Box component="span" sx={{ display: 'block' }}>
                          {savedLine(who(trip.savedBy), trip.updatedAt)}
                        </Box>
                      </>
                    )}
                    slotProps={{ primary: { noWrap: true }, secondary: { noWrap: true } }}
                  />
                  <IconButton size="small" edge="end" onClick={(e) => void removeTrip(trip.id, e)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </ListItemButton>
              ))}
            </List>
          </>
        )}
      </Paper>

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: { xs: anythingOpen ? 'flex' : 'none', md: 'flex' },
          flexDirection: { xs: 'column', lg: 'row' },
        }}
      >
        {anythingOpen && city ? (
          <>
            {showItinerary && (
            <Box
              sx={{
                width: { xs: '100%', lg: 400 },
                flexShrink: 0,
                overflowY: 'auto',
                p: 2.5,
                borderRight: { lg: 1 },
                borderColor: 'divider',
              }}
            >
              <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                <Button
                  size="small"
                  startIcon={<ArrowBackIcon />}
                  onClick={clearSelection}
                  sx={{ display: { xs: 'inline-flex', md: 'none' } }}
                >
                  All saved routes
                </Button>
                <Button
                  size="small"
                  color="inherit"
                  startIcon={<MapOutlinedIcon />}
                  onClick={() => setMobileView('map')}
                  sx={{ display: { xs: 'inline-flex', lg: 'none' } }}
                >
                  Map
                </Button>
                {/* Only when it is folded; otherwise this is the list beside it. */}
                {!listOpen && (
                  <Button
                    size="small"
                    startIcon={<ChevronRightIcon />}
                    onClick={() => setListOpen(true)}
                    sx={{ display: { xs: 'none', md: 'inline-flex' } }}
                  >
                    Show saved routes
                  </Button>
                )}
                {/*
                  The same export the planner offers, on the same terms: a
                  saved route carries the request it was made from, so the
                  turns can be asked for against the line that was drawn.

                  Only for a single route. A saved operation is several days,
                  each its own plan, and one file of all of them is a
                  different question.
                */}
                {selected && (
                  <ExportRouteButton
                    days={[{
                      label: '',
                      request: selected.request,
                      plan: selected.plan,
                      ...(selected.request.date ? { date: selected.request.date } : {}),
                    }]}
                    name={selected.name}
                    getMap={() => exportMap.current}
                  />
                )}
                {/* A saved operation exports every day it has. */}
                {selectedTrip && (
                  <ExportRouteButton
                    days={tripExportDays}
                    name={selectedTrip.name}
                    getMap={() => exportMap.current}
                    onShowDay={setTripDay}
                    activeDay={tripDay}
                  />
                )}
              </Stack>
              {/* Here only while the map is elsewhere. */}
              {dayStrip && (
                <Box sx={{ display: { xs: 'block', lg: 'none' }, mb: 2 }}>
                  {dayStrip}
                </Box>
              )}

              {viewingPlan ? (
                <ItineraryPanel
                  passedHidden={passedHidden}
                  basemapUrl={basemapUrlFor(city, settings['map.shared_basemap'] === true)}
                  {...(itineraryDirections ? { directionsFor: itineraryDirections } : {})}
                  plan={viewingPlan}
                  city={city}
                  onShowDetails={setDetailsPoiId}
                  onShowPoint={(index) => { setHighlighted(index); setPinIndex(index); }}
                  onStopHover={setHighlighted}
                  onStopFocus={focusOnMap}
                  onStopReveal={focusOnMap}
                  highlightedStopIndex={highlighted}
                  /* Coverage means covered by OTHER saved work; this round
                     covering itself is not news. */
                  {...(selected ?? selectedTrip
                    ? { coverageIgnoreId: (selected ?? selectedTrip)?.id ?? '' }
                    : {})}
                  {...(selectedTrip
                    ? { coverageDayNote: tripDayLabel(tripDay, viewingDay?.date ?? null) }
                    : {})}
                  {...(selected?.request.date ?? viewingDay?.date
                    ? { coverageRoundDate: selected?.request.date ?? viewingDay?.date ?? '' }
                    : {})}
                />
              ) : (
                /* A day that could not be planned says why, rather than
                   leaving the panel blank beside an empty map. */
                <Alert severity="warning" variant="outlined">
                  {viewingDay?.error ?? 'This day was not planned.'}
                </Alert>
              )}
            </Box>
            )}
            {showMap && (
            <Box sx={{ flex: 1, minWidth: 0, position: 'relative' }}>
              {/*
                Which day, over the map rather than beside it.

                The same place the planner keeps it, and for a reason that
                only shows on a phone: there the map REPLACES the column, so a
                strip living in the column could not be reached from the one
                view where somebody is looking at the day it selects. It is
                also too wide for that column, which gave it a scrollbar of
                its own.

                The wrapper ignores pointer events so the map stays draggable
                in the gaps between the cards.
              */}
              {dayStrip && (
                <Box
                  sx={{
                    display: { xs: 'none', lg: 'block' },
                    position: 'absolute',
                    zIndex: 2,
                    // Clear of the zoom on the left and Map only on the right.
                    top: 10, left: 52, right: 130,
                    pointerEvents: 'none',
                    '& > *': { pointerEvents: 'auto' },
                  }}
                >
                  {dayStrip}
                </Box>
              )}

              {/* The same control both planners carry, in the same corner. */}
              <MapOnlyToggle
                open={panelsOpen}
                onToggle={toggleMapOnly}
                whatIsBeside="Itinerary"
              />
              <MapView
                passedHidden={passedHidden}
                onPassedHiddenChange={setPassedHidden}
                focusStop={focusStop}
                onMapReady={(map) => { exportMap.current = map; }}
                city={city}
                plan={viewingPlan}
                basemapUrl={basemapUrlFor(city, settings['map.shared_basemap'] === true)}
                highlightedStopIndex={highlighted}
                /* The rest of the operation, faint, so a day is read in the
                   context of the week it belongs to. */
                otherDays={otherDays}
                /*
                 * A numbered marker opens the same dialog the name does. The
                 * two views are one itinerary, and a stop on the map is the
                 * obvious thing to ask about.
                 */
                onStopClick={(index) => {
                  const stop = viewingPlan?.stops[index];
                  if (stop?.poiId) setDetailsPoiId(stop.poiId);
                }}
              />
              {/* The way back, since the map has taken the whole screen. */}
              <Button
                variant="contained"
                size="small"
                startIcon={<ArrowBackIcon />}
                onClick={() => setMobileView('itinerary')}
                sx={{
                  display: { xs: 'inline-flex', lg: 'none' },
                  position: 'absolute',
                  top: 12,
                  left: 12,
                  zIndex: 6,
                  boxShadow: 3,
                }}
              >
                Itinerary
              </Button>
            </Box>
            )}
          </>
        ) : (
          <Stack spacing={1.5} sx={{ alignItems: 'center', justifyContent: 'center', flex: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Select a route or an operation to view it
            </Typography>
            {!listOpen && (
              <Button size="small" startIcon={<ChevronRightIcon />} onClick={() => setListOpen(true)}>
                Show saved routes
              </Button>
            )}
          </Stack>
        )}
      </Box>

      {/*
        The same dialog the planner uses, minus its buttons.
        A saved route is a record: there is nothing here to add a place to,
        so the place is shown and that is all.
      */}
      {/* A saved round carries its pins too, and they were as unopenable
          here as anywhere else. */}
      <PinnedStopDialog
        stop={pinIndex === null ? null : viewingPlan?.stops[pinIndex] ?? null}
        cityId={city?.id ?? null}
        onClose={() => setPinIndex(null)}
      />

      <PlaceDetailsDialog
        poiId={detailsPoiId}
        onClose={() => setDetailsPoiId(null)}
        city={city}
        {...(city ? { basemapUrl: basemapUrlFor(city, settings['map.shared_basemap'] === true) } : {})}
      />
    </Box>
  );
}
